import { _, helpers, mui, React, ts, ui } from '_core';

import { transform } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  portfolios: string;
  fullScreen: boolean;
  hasBenchmark: boolean;
  isBasket: boolean;
  isCombinedBasket: boolean;
  isLongOnly: boolean;
  scale: ts.enums.SCALE_ENUM;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  title: string;
  params: Params;
};

const Chart = ({
  id,
  readOnly,
  fullScreen,
  loadWidgetData,
  portfolios,
  hasBenchmark = false,
  isBasket,
  isCombinedBasket,
  isLongOnly,
  title,
  params,
}: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [filteredData, setFilteredData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [selectedTab, setSelectedTab] = React.useState<'assets' | 'etf'>('assets');
  const availableTabs = isCombinedBasket ? ['assets', 'etf'] : ['assets'];

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: portfolios,
        },
      });
      if (response.data) setData(response.data);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (portfolios) loadData();
    return () => setData(null);
  }, [portfolios]);

  React.useEffect(() => {
    if (!_.isNil(data)) {
      const localFilteredData = data.map((el) =>
        _.pickBy(
          el,
          (_, key) =>
            key == 'date' ||
            (isCombinedBasket && key.startsWith('etf_')) ||
            (!isCombinedBasket && !key.startsWith('etf_'))
        )
      );

      setFilteredData(
        transform(localFilteredData, isBasket, selectedTab == 'etf', hasBenchmark, isLongOnly, params.showTargetWeight)
      );
    }
  }, [data, selectedTab]);

  const getTabs = () => {
    if (availableTabs.length == 1) {
      return undefined;
    }
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(_e, newTab) => setSelectedTab(newTab)}
      >
        <mui.core.Tab label="Assets" value="assets" />
        <mui.core.Tab label="Etf Components" value="etf" />
      </mui.core.Tabs>
    );
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.LineChart
          id={id}
          data={filteredData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
